import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import BasicDetails from "./BasicDetails";
import ProgressMap from "components/main-view/utils/components/ProgressMap";
import ClassParentDetails from "./ClassParentDetails";
import AdditionalDetails from "./AdditionalDetails";
import SavingPopUp from "../pop-up-panels/saving-pop-up";
import { ClassListType, ContactListType } from "utils/interfaces";
import { createRecordRequest, updateRecordRequest } from "services/apiRequests";
import { formatDateString } from "utils/formatDate";
import { capitaliseFirstChar, singlify } from "utils/utils";
import { formatUpdateStudent } from "utils/formatUpdate";
import { MainContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import { AnimatePresence } from "framer-motion";

interface AddNewStudentProps {
  ref?: any;
  selectedData?: any;
  afterCreateFunction: () => void;
  headerRef: React.MutableRefObject<any>;
}

const AddNewStudent: React.FC<AddNewStudentProps> = forwardRef(
  ({ selectedData, afterCreateFunction, headerRef }, ref) => {
    // States definition
    const [firstName, setFirstName] = useState("");
    const [middleName, setMiddleName] = useState("");
    const [lastName, setLastName] = useState("");
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [usernameAvailable, setUsernameAvailable] = useState<
      "waiting" | "available" | "unavailable" | "error" | "unmodified"
    >(!!selectedData ? "unmodified" : "waiting");
    const [emailValidation, setEmailValidation] = useState<
      | "waiting"
      | "not-valid"
      | "unavailable"
      | "available"
      | "error"
      | "unmodified"
    >("waiting");
    const [dob, setDob] = useState("");
    const [gender, setGender] = useState("");
    const [selectedClasses, setSelectedClasses] = useState<ClassListType[]>([]);
    const [selectedContacts, setSelectedContacts] = useState<ContactListType[]>(
      []
    );
    const [
      isAboriginalOrTorresStraitIslander,
      setIsAboriginalOrTorresStraitIslander,
    ] = useState(true);
    const [notes, setNotes] = useState("");
    // Flow control states
    const [stage, setStage] = useState(1);
    const [isSaving, setIsSaving] = useState(false);

    // Get the values from the context
    const { classesTerminology } = usePlatform();
    const { contextSchoolByRole, userRole } = useContext(MainContext);

    /**
     * Functions to externalise using refs
     */
    useImperativeHandle(ref, () => ({
      // Function to present all the gathered data in the form
      provideData() {
        return provideData();
      },
      handleSaveNewStudent() {
        handleSave();
      },
    }));

    /**
     * Function to activate saveButton on header
     */
    useEffect(() => {
      // Define the condition to consider an ok the username
      const usernameCheck = ["available", "waiting", "unmodified"].includes(
        usernameAvailable
      );

      // Set a different behaviour when editing
      const disabledCondition = !!selectedData
        ? !(firstName && lastName && usernameCheck)
        : !(firstName && lastName && usernameCheck && stage === 3);

      // Condition definition
      headerRef.current.options().setSaveButton({
        show: true,
        disabled: disabledCondition,
      });
    }, [firstName, lastName, stage, usernameAvailable]);

    /**
     * If there is selected data, then prefill the defined fields
     */
    useEffect(() => {
      if (selectedData) {
        setFirstName(selectedData.firstName || "");
        setMiddleName(selectedData.middleName || "");
        setLastName(selectedData.lastName || "");
        setUsername(selectedData.username || "");
        setEmail(selectedData.email || "");
        setDob(formatDateString(selectedData.dateOfBirth) || "");
        setGender(
          selectedData.gender ? capitaliseFirstChar(selectedData.gender) : ""
        );
        setSelectedClasses(selectedData.classes || "");
        setSelectedContacts(selectedData.parents || "");
        setIsAboriginalOrTorresStraitIslander(selectedData.heritage || false);
        setNotes(selectedData.notes ? selectedData.notes.join("\n\n") : "");
      }
    }, []);

    /**
     * Function to handle save pressing
     */
    const handleSave = async () => {
      // Check if the function is already saving
      if (isSaving) return;

      // Block this function
      setIsSaving(true);

      // Get the data from the settings form
      const data: any = provideData();

      // Request for creating new student
      let response = { successful: false, message: "" };
      if (!selectedData) {
        response = await createRecordRequest(data, "/api/users/students");
      } else {
        delete data.organisations
        delete data.classes
        delete data.parents
        response = await updateRecordRequest(
          { _id: selectedData._id, toUpdate: data },
          "/api/users/students"
        );
      }

      // Release the function
      setIsSaving(false);

      // Check if the response is successful
      if (!response.successful) {
        // alert(response.message);
        return;
      }

      // Execute the function to use after create/edit
      afterCreateFunction();
    };

    /**
     * Function that returns all the form data
     * @returns data
     */
    const provideData = () => {
      // Get just the ids from the classes
      const classIds = selectedClasses
        ? selectedClasses.map((item: ClassListType) => item._id)
        : [];
      // Get just the ids from the contacts
      const contactIds = selectedContacts
        ? selectedContacts.map((contact: ContactListType) => contact._id)
        : [];

      // Return all the gathered data in the form
      const dataToProvide = {
        firstName: firstName,
        middleName: middleName,
        lastName: lastName,
        username: email ? undefined : username ? username : undefined, // if email is required, force to undefined
        email: email ? email : undefined,
        dateOfBirth: dob,
        gender: gender.toLowerCase(),
        classes: classIds,
        parents: contactIds,
        heritage: isAboriginalOrTorresStraitIslander,
        notes: [notes],
        role: "student"
      };

      // Define school, only to be used for master admins in backend
      if (userRole === "master admin" && contextSchoolByRole && contextSchoolByRole._id) {
        (dataToProvide as any).organisations = [contextSchoolByRole._id];
      }

      // Formatting the data to be sent as a request
      const formattedData = formatUpdateStudent(selectedData, dataToProvide);

      return formattedData;
    };

    return (
      <div className="w-full h-full flex p-4 justify-center items-start overflow-x-auto custom-scroll">
        {stage === 1 ? (
          <BasicDetails
            selectedStudent={selectedData}
            firstName={firstName}
            middleName={middleName}
            lastName={lastName}
            username={username}
            usernameAvailable={usernameAvailable}
            email={email}
            emailValidation={emailValidation}
            dob={dob}
            gender={gender}
            setFirstName={setFirstName}
            setMiddleName={setMiddleName}
            setLastName={setLastName}
            setUsername={setUsername}
            setUsernameAvailable={setUsernameAvailable}
            setEmail={setEmail}
            setEmailValidation={setEmailValidation}
            setDob={setDob}
            setGender={setGender}
            setStage={setStage}
          />
        ) : stage === 2 ? (
          <ClassParentDetails
            selectedClasses={selectedClasses}
            selectedContacts={selectedContacts}
            setStage={setStage}
            setSelectedClasses={setSelectedClasses}
            setSelectedContacts={setSelectedContacts}
          />
        ) : (
          <AdditionalDetails
            isAboriginalOrTorresStraitIslander={
              isAboriginalOrTorresStraitIslander
            }
            notes={notes}
            setIsAboriginalOrTorresStraitIslander={
              setIsAboriginalOrTorresStraitIslander
            }
            setNotes={setNotes}
            setStage={setStage}
            handleSave={handleSave}
          />
        )}
        <ProgressMap
          stage={stage}
          steps={[
            "Basic details",
            `${singlify(classesTerminology)} details`,
            "Additional details",
          ]}
        />
        {/* Screen to wait until the school is created */}
        <AnimatePresence>
          {isSaving && (
            <SavingPopUp
              key="saving-pop-up"
              onBgClick={() => setIsSaving(false)}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }
);

export default AddNewStudent;
